import gatewayClient, { callMCPTool } from './gateway';
import { findCustomerByCipp } from '../../utils/findCustomerByCipp';

const SERVER_NAME = 'Odin-MCP';

/**
 * Gateway base URL for all API calls including PDF downloads.
 * PDFs are proxied through the gateway (/pdf/:id) so the frontend
 * never talks to Odin-MCP directly.
 * Authentication uses OAuth2 Bearer tokens from MSAL.
 */
const CSG_GATEWAY_URL = import.meta.env.VITE_CSG_GATEWAY_URL || '/api';

/**
 * Odin-MCP API - MSR report generation and aggregation
 */

/**
 * Build a PDF download URL from a PDF ID
 * @param {string} pdfId - PDF ID from report generation
 * @returns {string} - Full download URL
 */
export const buildPdfDownloadUrl = (pdfId) => {
  if (!pdfId) return null;
  const cleanId = pdfId.replace(/^\/?(pdf\/)?/, '');
  return `${CSG_GATEWAY_URL}/pdf/${cleanId}`;
};

/**
 * Resolve CIPP tenant ID to Odin customer ID
 * Fetches customer list and finds matching customer by CIPP platform ID
 * @param {string} cippTenantId - CIPP tenant ID
 * @returns {Promise<string|null>} - Odin customer ID or null
 */
export const resolveOdinCustomerId = async (cippTenantId) => {
  try {
    // Fetch all customers to find matching CIPP tenant
    const result = await callMCPTool(SERVER_NAME, 'list_customers');

    if (!result?.customers || !Array.isArray(result.customers)) {
      console.warn('[odinApi] No customers found in response');
      return null;
    }

    // Find customer with matching CIPP platform ID (case-insensitive)
    const customer = findCustomerByCipp(result.customers, cippTenantId);

    if (customer) {
      console.log('[odinApi] Resolved CIPP tenant to customer:', {
        cippTenantId,
        customerId: customer.id,
        displayName: customer.displayName,
      });
      return customer.id;
    }

    console.warn('[odinApi] No customer found for CIPP tenant ID:', cippTenantId);
    return null;
  } catch (error) {
    console.error('[odinApi] Error resolving customer ID:', error);
    return null;
  }
};

/**
 * Generate unified MSR report from a CIPP tenant ID
 * Handles customer resolution and calls the unified MSR tool
 * @param {string} cippTenantId - CIPP tenant ID
 * @param {object} options - Report options (customerName, outputFormat, reportPeriod)
 * @returns {Promise<object>} - Report result with pdfId or data
 */
export const generateUnifiedMSRFromTenant = async (cippTenantId, options = {}) => {
  const { customerName, outputFormat = 'pdf', reportPeriod } = options;

  // Resolve CIPP tenant ID to Odin customer ID first
  const customerId = await resolveOdinCustomerId(cippTenantId);

  if (!customerId) {
    throw new Error(`Could not resolve customer for tenant ID: ${cippTenantId}`);
  }

  // Call the unified MSR generation tool with correct camelCase parameters
  // Use 300 second timeout - MSR generation collects from 5+ MCP servers, generates PDF,
  // and may spend up to 40s on Autotask time-entry fetching (500ms/call × ~50+ tickets)
  const result = await callMCPTool(SERVER_NAME, 'generate_unified_msr', {
    customerId,
    customerName,
    outputFormat,
    reportPeriod,
  }, { timeout: 300000 });

  return result;
};

/**
 * Generate CE Readiness Report PDF for a customer
 * Calls Odin-MCP's generate_ce_readiness_report tool and returns pdfId for download.
 * @param {string} cippTenantId - CIPP tenant UUID (from selectedTenant.id)
 * @param {object} options - { customerName }
 * @returns {Promise<object>} - { pdfId, downloadUrl, overallStatus, gapCount, scannedAt }
 */
export const generateCeReadinessReport = async (cippTenantId, options = {}) => {
  const { customerName } = options;

  const customerId = await resolveOdinCustomerId(cippTenantId);
  if (!customerId) {
    throw new Error(`Could not resolve customer for tenant ID: ${cippTenantId}`);
  }

  // 120s timeout — CE report is fast (DB lookup + Puppeteer), but allow headroom
  const result = await callMCPTool(SERVER_NAME, 'generate_ce_readiness_report', {
    customerId,
    customerName,
  }, { timeout: 120000 });

  return result;
};

/**
 * Generate MSR report for a customer
 * @param {string} customerId - Customer ID
 * @param {object} options - Report options
 */
export const generateMSRReport = async (customerId, options = {}) => {
  return callMCPTool(SERVER_NAME, 'generate_msr_report', {
    customer_id: customerId,
    ...options,
  });
};

/**
 * Get report generation status
 * @param {string} reportId - Report ID
 */
export const getReportStatus = async (reportId) => {
  return callMCPTool(SERVER_NAME, 'get_report_status', { report_id: reportId });
};

/**
 * List generated reports for a customer
 * @param {string} customerId - Customer ID
 */
export const listReports = async (customerId) => {
  return callMCPTool(SERVER_NAME, 'list_reports', { customer_id: customerId });
};

/**
 * Get aggregated health score for a customer
 * @param {string} customerId - Customer ID
 */
export const getHealthScore = async (customerId) => {
  return callMCPTool(SERVER_NAME, 'get_health_score', { customer_id: customerId });
};

/**
 * Get all customers
 */
export const listCustomers = async () => {
  return callMCPTool(SERVER_NAME, 'list_customers');
};

/**
 * Get customer summary
 * @param {string} customerId - Customer ID
 */
export const getCustomerSummary = async (customerId) => {
  return callMCPTool(SERVER_NAME, 'get_customer_summary', { customer_id: customerId });
};

/**
 * Extract base64 PDF data from HTML response
 * The Odin-MCP /pdf/:pdfId endpoint returns HTML containing base64-encoded PDF
 * Format: data:application/pdf;base64,<base64_data>
 * @param {string} htmlContent - HTML response from PDF endpoint
 * @returns {string|null} - Base64 PDF data or null if not found
 */
const extractBase64FromHtml = (htmlContent) => {
  if (!htmlContent || typeof htmlContent !== 'string') {
    return null;
  }

  // Match the base64 PDF data pattern
  const base64Pattern = /data:application\/pdf;base64,([A-Za-z0-9+/=]+)/;
  const match = htmlContent.match(base64Pattern);

  if (match && match[1]) {
    return match[1];
  }

  return null;
};

/**
 * Convert base64 string to Blob
 * @param {string} base64Data - Base64 encoded data
 * @param {string} contentType - MIME type
 * @returns {Blob} - Blob object
 */
const base64ToBlob = (base64Data, contentType = 'application/pdf') => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }

  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

/**
 * Download PDF report from Odin-MCP
 * Fetches PDF from the /pdf/:pdfId endpoint, extracts base64 data from HTML response,
 * creates a blob, and triggers browser download.
 *
 * @param {string} pdfId - PDF ID (from downloadUrl like "/pdf/abc-123")
 * @param {string} [fileName='report.pdf'] - Filename for the downloaded file
 * @returns {Promise<{ success: boolean, error?: string }>} - Download result
 *
 * @example
 * // From a report object with downloadUrl: "/pdf/abc-123-def"
 * const result = await downloadReportPdf('abc-123-def', 'Customer-MSR-Report.pdf');
 * if (!result.success) {
 *   console.error('Download failed:', result.error);
 * }
 */
export const downloadReportPdf = async (pdfId, fileName = 'report.pdf') => {
  try {
    if (!pdfId) {
      return { success: false, error: 'PDF ID is required' };
    }

    // Clean up pdfId - remove leading slash and /pdf/ prefix if present
    const cleanPdfId = pdfId.replace(/^\/?(pdf\/)?/, '');

    // Get OAuth2 token from localStorage
    const accessToken = localStorage.getItem('accessToken');

    // Fetch PDF through the gateway proxy
    const response = await fetch(`${CSG_GATEWAY_URL}/pdf/${cleanPdfId}`, {
      method: 'GET',
      headers: {
        Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      if (response.status === 404) {
        return {
          success: false,
          error: 'PDF not found. The report may have expired. Please regenerate the report.',
        };
      }
      return {
        success: false,
        error: `Failed to fetch PDF: ${response.status} ${response.statusText}`,
      };
    }

    const htmlContent = await response.text();

    // Extract base64 PDF data from HTML response
    const base64Data = extractBase64FromHtml(htmlContent);

    if (!base64Data) {
      return {
        success: false,
        error: 'PDF data not found in response. The report may have expired.',
      };
    }

    // Convert base64 to Blob
    const blob = base64ToBlob(base64Data);

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('[odinApi] downloadReportPdf error:', error);
    return {
      success: false,
      error: error.message || 'Failed to download PDF',
    };
  }
};

/**
 * Download PDF report using downloadUrl from report object
 * Convenience wrapper that extracts pdfId from a full downloadUrl
 *
 * @param {string} downloadUrl - Full download URL (e.g., "/pdf/abc-123-def")
 * @param {string} [fileName='report.pdf'] - Filename for the downloaded file
 * @returns {Promise<{ success: boolean, error?: string }>} - Download result
 *
 * @example
 * // From a report object
 * const result = await downloadReportFromUrl(report.downloadUrl, `${report.customerName}-MSR.pdf`);
 */
export const downloadReportFromUrl = async (downloadUrl, fileName = 'report.pdf') => {
  if (!downloadUrl) {
    return { success: false, error: 'Download URL is required' };
  }

  // Extract pdfId from URL path (e.g., "/pdf/abc-123" -> "abc-123")
  const pdfIdMatch = downloadUrl.match(/\/pdf\/([^/?]+)/);
  const pdfId = pdfIdMatch ? pdfIdMatch[1] : downloadUrl;

  return downloadReportPdf(pdfId, fileName);
};

// =============================================================================
// Report Recommendations API
// =============================================================================

/**
 * List all active recommendation templates, optionally filtered by category
 * @param {string} [category] - Optional category filter (e.g., "Microsoft 365", "Cyber Security", "AI")
 * @returns {Promise<object>} - { success, templates, count }
 */
export const listRecommendationTemplates = async (category) => {
  const args = {};
  if (category) args.category = category;
  return callMCPTool(SERVER_NAME, 'list_recommendation_templates', args);
};

/**
 * List available recommendation categories
 * @returns {Promise<object>} - { success, categories, count }
 */
export const listRecommendationCategories = async () => {
  return callMCPTool(SERVER_NAME, 'list_recommendation_categories', {});
};

/**
 * Create a new recommendation template (Admin only)
 * @param {object} templateData - Template data
 * @param {string} templateData.platform - Platform (cipp, sophos, acronis, autotask, scalepad, general)
 * @param {string} templateData.title - Template title
 * @param {string} templateData.html_content - HTML content for the card
 * @param {string} [templateData.description] - Brief description
 * @param {object} [templateData.variables] - Variable definitions with defaults
 * @param {string} [templateData.icon] - Emoji icon
 * @param {number} [templateData.display_order] - Sort order
 * @returns {Promise<object>} - { success, template }
 */
export const createRecommendationTemplate = async (templateData) => {
  return callMCPTool(SERVER_NAME, 'create_recommendation_template', templateData);
};

/**
 * Soft-delete a recommendation template (Admin only)
 * @param {string} templateId - UUID of the template
 * @returns {Promise<object>} - { success, deleted, message }
 */
export const deleteRecommendationTemplate = async (templateId) => {
  return callMCPTool(SERVER_NAME, 'delete_recommendation_template', { templateId });
};

/**
 * Save recommendation placements for a report
 * @param {string} reportId - UUID of the saved report
 * @param {Array<object>} recommendations - Array of placements
 * @param {string} recommendations[].template_id - Template UUID
 * @param {number} recommendations[].page_number - Page (1-3)
 * @param {number} recommendations[].zone_number - Zone (1-N based on layout)
 * @param {string} recommendations[].timeframe - Timeframe selection
 * @param {string} [recommendations[].timeframe_custom] - Custom timeframe text
 * @param {object} [recommendations[].variable_values] - Variable overrides
 * @param {string} [layout='2x3'] - Grid layout key: 2x3, 1x3, 3x2, 2x2
 * @returns {Promise<object>} - { success, reportId, layout, savedCount, recommendations }
 */
export const saveReportWithRecommendations = async (reportId, recommendations, layout = '2x3') => {
  return callMCPTool(SERVER_NAME, 'save_report_with_recommendations', {
    reportId,
    recommendations,
    layout,
  });
};

/**
 * Generate recommendation pages PDF, merge with base MSR, and cache
 * @param {string} reportId - UUID of the saved report with recommendations
 * @returns {Promise<object>} - { success, pdfId, pageCount, downloadUrl }
 */
export const generateRecommendationsPdf = async (reportId) => {
  return callMCPTool(SERVER_NAME, 'generate_recommendations_pdf', { reportId }, { timeout: 60000 });
};

/**
 * Get a saved report by ID including recommendation placements
 * @param {string} reportId - UUID of the saved report
 * @returns {Promise<object>} - { success, report }
 */
export const getSavedReport = async (reportId) => {
  return callMCPTool(SERVER_NAME, 'get_saved_report', { reportId });
};

export default {
  buildPdfDownloadUrl,
  resolveOdinCustomerId,
  generateUnifiedMSRFromTenant,
  generateCeReadinessReport,
  generateMSRReport,
  getReportStatus,
  listReports,
  getHealthScore,
  listCustomers,
  getCustomerSummary,
  downloadReportPdf,
  downloadReportFromUrl,
  listRecommendationTemplates,
  createRecommendationTemplate,
  deleteRecommendationTemplate,
  saveReportWithRecommendations,
  generateRecommendationsPdf,
  getSavedReport,
};
